import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

const NAVY  = '#0D2B5E';
const BLUE  = '#1A6BB5';
const GREEN = '#6DC234';
const GREEN_DIM = '#5AAD28';

const Settings = ({ setPage }) => {
  const { user, organization, token } = useAuth();
  const API_URL = process.env.REACT_APP_API_URL || 'https://api.jaychrisedi.com';

  const [activeTab, setActiveTab] = useState('account');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const [accountForm, setAccountForm] = useState({
    firstName: user?.firstName || '',
    lastName:  user?.lastName  || '',
    email:     user?.email     || ''
  });

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [orgForm, setOrgForm] = useState({
    name:               organization?.name               || '',
    shopifyDomain:      organization?.shopifyDomain      || '',
    shopifyClientId:    organization?.shopifyClientId    || '',
    shopifyClientSecret:organization?.shopifyClientSecret|| ''
  });

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  };

  const handleUpdateAccount = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(API_URL + '/api/auth/update-profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
        body: JSON.stringify(accountForm)
      });
      if (res.ok) {
        const data = await res.json();
        localStorage.setItem('user', JSON.stringify(data.user));
        showMessage('Account updated successfully');
      } else {
        const data = await res.json();
        showMessage(data.error || 'Update failed', 'error');
      }
    } catch { showMessage('Network error. Please try again.', 'error'); }
    setSaving(false);
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) { showMessage('Passwords do not match', 'error'); return; }
    if (passwordForm.newPassword.length < 8) { showMessage('Password must be at least 8 characters', 'error'); return; }
    setSaving(true);
    try {
      const res = await fetch(API_URL + '/api/auth/update-password', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
        body: JSON.stringify({ currentPassword: passwordForm.currentPassword, newPassword: passwordForm.newPassword })
      });
      if (res.ok) {
        showMessage('Password updated successfully');
        setPasswordForm({ currentPassword:'', newPassword:'', confirmPassword:'' });
      } else {
        const data = await res.json();
        showMessage(data.error || 'Password update failed', 'error');
      }
    } catch { showMessage('Network error. Please try again.', 'error'); }
    setSaving(false);
  };

  const handleUpdateOrg = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(API_URL + '/api/auth/update-organization', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
        body: JSON.stringify(orgForm)
      });
      if (res.ok) {
        const data = await res.json();
        localStorage.setItem('organization', JSON.stringify(data.organization));
        showMessage('Organization updated successfully');
      } else {
        const data = await res.json();
        showMessage(data.error || 'Update failed', 'error');
      }
    } catch { showMessage('Network error. Please try again.', 'error'); }
    setSaving(false);
  };

  const handleUpgrade = async (plan) => {
    try {
      const res = await fetch(API_URL + '/api/billing/create-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
        body: JSON.stringify({ plan })
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else showMessage(data.error || 'Failed to start checkout', 'error');
    } catch { showMessage('Network error. Please try again.', 'error'); }
  };

  const handleManageBilling = async () => {
    try {
      const res = await fetch(API_URL + '/api/billing/portal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token }
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else showMessage(data.error || 'Failed to open billing portal', 'error');
    } catch { showMessage('Network error. Please try again.', 'error'); }
  };

  /* ── Shared styles ─────────────────────────────────────────── */
  const inputStyle = {
    width:'100%', padding:'10px 14px', borderRadius:'8px',
    border:'1px solid #E2E8F0', backgroundColor:'#F8FAFD',
    color:NAVY, fontSize:'14px', outline:'none', boxSizing:'border-box'
  };
  const labelStyle = { display:'block', fontSize:'13px', fontWeight:'600', color:'#64748B', marginBottom:'6px' };
  const fieldGroup = { marginBottom:'20px' };
  const btnPrimary = {
    display:'inline-flex', alignItems:'center', gap:'8px', padding:'10px 24px',
    backgroundColor:BLUE, color:'white', border:'none', borderRadius:'8px',
    cursor:'pointer', fontSize:'14px', fontWeight:'600', opacity:saving?0.7:1
  };
  const sectionTitle = { color:NAVY, fontSize:'20px', fontWeight:'700', marginBottom:'24px' };
  const divider = { borderTop:'1px solid #E2E8F0', marginTop:'32px', paddingTop:'32px' };

  const tabs = [
    { id:'account',      label:'Account'      },
    { id:'password',     label:'Password'     },
    { id:'organization', label:'Organization' },
    { id:'billing',      label:'Billing'      },
  ];

  return (
    <div style={{ minHeight:'100vh', backgroundColor:'#F4F6F9', padding:'32px 24px' }}>
      <div style={{ maxWidth:'960px', margin:'0 auto' }}>

        {/* Header */}
        <div style={{ marginBottom:'32px' }}>
          <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:'11px', fontWeight:'700', color:BLUE, letterSpacing:'0.12em', textTransform:'uppercase', marginBottom:'4px' }}>Account</div>
          <h1 style={{ color:NAVY, fontSize:'28px', fontWeight:'800', margin:0, letterSpacing:'-0.03em' }}>Settings</h1>
        </div>

        {/* Toast */}
        {message && (
          <div style={{
            padding:'12px 16px', borderRadius:'8px', marginBottom:'24px', fontSize:'14px', fontWeight:'500',
            backgroundColor: message.type==='success' ? '#F0FDF4' : '#FEF2F2',
            color:           message.type==='success' ? '#166534'  : '#991B1B',
            border:          message.type==='success' ? '1px solid rgba(109,194,52,0.3)' : '1px solid rgba(239,68,68,0.3)'
          }}>
            {message.text}
          </div>
        )}

        <div style={{ display:'flex', gap:'28px' }}>

          {/* Tab nav */}
          <div style={{ width:'188px', flexShrink:0 }}>
            {tabs.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
                display:'flex', alignItems:'center', width:'100%',
                padding:'11px 16px', marginBottom:'4px', borderRadius:'8px',
                backgroundColor: activeTab===tab.id ? '#EFF6FF' : 'transparent',
                color:           activeTab===tab.id ? BLUE       : '#64748B',
                border:          activeTab===tab.id ? `1px solid rgba(26,107,181,0.2)` : '1px solid transparent',
                cursor:'pointer', fontSize:'14px', fontWeight: activeTab===tab.id ? '600' : '400',
                textAlign:'left'
              }}>
                {tab.label}
              </button>
            ))}
          </div>

          {/* Panel */}
          <div style={{ flex:1, backgroundColor:'white', borderRadius:'14px', padding:'32px', border:'1px solid #E2E8F0', boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>

            {activeTab === 'account' && (
              <form onSubmit={handleUpdateAccount}>
                <h2 style={sectionTitle}>Account Information</h2>
                <div style={{ display:'flex', gap:'16px' }}>
                  <div style={{ ...fieldGroup, flex:1 }}>
                    <label style={labelStyle}>First Name</label>
                    <input type="text" value={accountForm.firstName} onChange={e => setAccountForm({...accountForm, firstName:e.target.value})} style={inputStyle}/>
                  </div>
                  <div style={{ ...fieldGroup, flex:1 }}>
                    <label style={labelStyle}>Last Name</label>
                    <input type="text" value={accountForm.lastName} onChange={e => setAccountForm({...accountForm, lastName:e.target.value})} style={inputStyle}/>
                  </div>
                </div>
                <div style={fieldGroup}>
                  <label style={labelStyle}>Email</label>
                  <input type="email" value={accountForm.email} onChange={e => setAccountForm({...accountForm, email:e.target.value})} style={inputStyle}/>
                </div>
                <button type="submit" disabled={saving} style={btnPrimary}>{saving ? 'Saving…' : 'Save Changes'}</button>
              </form>
            )}

            {activeTab === 'password' && (
              <form onSubmit={handleUpdatePassword}>
                <h2 style={sectionTitle}>Change Password</h2>
                <div style={fieldGroup}>
                  <label style={labelStyle}>Current Password</label>
                  <input type="password" value={passwordForm.currentPassword} onChange={e => setPasswordForm({...passwordForm, currentPassword:e.target.value})} style={inputStyle} required/>
                </div>
                <div style={fieldGroup}>
                  <label style={labelStyle}>New Password</label>
                  <input type="password" value={passwordForm.newPassword} onChange={e => setPasswordForm({...passwordForm, newPassword:e.target.value})} style={inputStyle} required minLength={8}/>
                </div>
                <div style={fieldGroup}>
                  <label style={labelStyle}>Confirm New Password</label>
                  <input type="password" value={passwordForm.confirmPassword} onChange={e => setPasswordForm({...passwordForm, confirmPassword:e.target.value})} style={inputStyle} required/>
                </div>
                <button type="submit" disabled={saving} style={btnPrimary}>{saving ? 'Updating…' : 'Update Password'}</button>
              </form>
            )}

            {activeTab === 'organization' && (
              <div>
                <h2 style={sectionTitle}>Organization & Shopify</h2>
                <form onSubmit={handleUpdateOrg}>
                  <div style={fieldGroup}>
                    <label style={labelStyle}>Company Name</label>
                    <input type="text" value={orgForm.name} onChange={e => setOrgForm({...orgForm, name:e.target.value})} style={inputStyle}/>
                  </div>
                  <button type="submit" disabled={saving} style={btnPrimary}>{saving ? 'Saving…' : 'Save Company Name'}</button>
                </form>
                <div style={divider}>
                  <h3 style={{ color:NAVY, fontSize:'17px', fontWeight:'700', marginBottom:'16px' }}>Shopify Connection</h3>
                  <div style={fieldGroup}>
                    <label style={labelStyle}>Store Domain</label>
                    <input type="text" placeholder="your-store.myshopify.com" value={orgForm.shopifyDomain} onChange={e => setOrgForm({...orgForm, shopifyDomain:e.target.value})} style={inputStyle}/>
                  </div>
                  <div style={fieldGroup}>
                    <label style={labelStyle}>Client ID</label>
                    <input type="text" placeholder="From Shopify Partners app credentials" value={orgForm.shopifyClientId} onChange={e => setOrgForm({...orgForm, shopifyClientId:e.target.value})} style={inputStyle}/>
                  </div>
                  <div style={fieldGroup}>
                    <label style={labelStyle}>Client Secret</label>
                    <input type="password" placeholder="shpss_xxxxxxxxxxxxx" value={orgForm.shopifyClientSecret} onChange={e => setOrgForm({...orgForm, shopifyClientSecret:e.target.value})} style={inputStyle}/>
                    <span style={{ fontSize:'12px', color:'#94A3B8', marginTop:'4px', display:'block' }}>Find these in Shopify Partners → Apps → Your App → Credentials</span>
                  </div>
                  <button type="button" disabled={saving} onClick={async () => {
                    setSaving(true);
                    try {
                      const res = await fetch(API_URL+'/api/shopify/connect', {
                        method:'POST', headers:{'Content-Type':'application/json','Authorization':'Bearer '+token},
                        body:JSON.stringify({ shop:orgForm.shopifyDomain, clientId:orgForm.shopifyClientId, clientSecret:orgForm.shopifyClientSecret })
                      });
                      const data = await res.json();
                      if (data.success) showMessage('Shopify connected: '+data.shopName);
                      else showMessage(data.error||'Connection failed','error');
                    } catch(err) { showMessage('Connection error: '+err.message,'error'); }
                    setSaving(false);
                  }} style={{ ...btnPrimary, backgroundColor:GREEN }}>
                    {saving ? 'Connecting…' : 'Connect Shopify Store'}
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'billing' && (
              <div>
                <h2 style={sectionTitle}>Billing and Subscription</h2>
                {/* Current plan */}
                <div style={{ padding:'24px', backgroundColor:'#F8FAFD', borderRadius:'12px', border:'1px solid #E2E8F0', marginBottom:'28px' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <div>
                      <div style={{ color:'#64748B', fontSize:'13px', marginBottom:'4px' }}>Current Plan</div>
                      <div style={{ color:NAVY, fontSize:'24px', fontWeight:'700' }}>
                        {organization?.plan === 'trial'
                          ? 'Free Trial'
                          : (organization?.plan ? organization.plan.charAt(0).toUpperCase()+organization.plan.slice(1) : 'Trial')}
                      </div>
                    </div>
                    <div style={{ display:'flex', gap:'12px', alignItems:'center' }}>
                      <div style={{ padding:'6px 14px', backgroundColor:'rgba(109,194,52,0.1)', color:GREEN_DIM, borderRadius:'20px', fontSize:'13px', fontWeight:'600', border:'1px solid rgba(109,194,52,0.25)' }}>Active</div>
                      {organization?.plan !== 'trial' && (
                        <button onClick={handleManageBilling} style={{ padding:'6px 14px', backgroundColor:'transparent', color:'#64748B', border:'1px solid #E2E8F0', borderRadius:'6px', cursor:'pointer', fontSize:'13px' }}>
                          Manage Billing
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <h3 style={{ color:NAVY, fontSize:'16px', fontWeight:'700', marginBottom:'16px' }}>Available Plans</h3>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'16px' }}>
                  {[
                    { name:'Starter',    key:'starter',    price:'$299', desc:'Up to 5 retailers',  featured:false },
                    { name:'Pro',        key:'pro',        price:'$399', desc:'Up to 10 retailers', featured:true  },
                    { name:'Enterprise', key:'enterprise', price:'$499', desc:'Unlimited retailers', featured:false },
                  ].map(plan => (
                    <div key={plan.name} style={{
                      padding:'24px', backgroundColor:'white', borderRadius:'12px',
                      border: plan.featured ? `2px solid ${BLUE}` : '1px solid #E2E8F0',
                      boxShadow: plan.featured ? `0 0 0 1px rgba(26,107,181,0.08),0 8px 24px rgba(26,107,181,0.1)` : '0 1px 4px rgba(0,0,0,0.04)',
                      position:'relative'
                    }}>
                      {plan.featured && (
                        <div style={{ fontSize:'10px', fontWeight:'700', color:BLUE, textTransform:'uppercase', letterSpacing:'1px', marginBottom:'8px', fontFamily:"'JetBrains Mono',monospace" }}>Most Popular</div>
                      )}
                      <div style={{ color:NAVY, fontSize:'17px', fontWeight:'700', marginBottom:'4px' }}>{plan.name}</div>
                      <div style={{ color:BLUE, fontSize:'28px', fontWeight:'800', marginBottom:'4px', fontFamily:"'JetBrains Mono',monospace" }}>
                        {plan.price}<span style={{ fontSize:'14px', color:'#94A3B8', fontWeight:'400' }}>/mo</span>
                      </div>
                      <div style={{ color:'#64748B', fontSize:'13px', marginBottom:'20px' }}>{plan.desc}</div>
                      <button onClick={() => organization?.plan !== plan.key && handleUpgrade(plan.key)} style={{
                        width:'100%', padding:'10px', borderRadius:'8px', cursor:'pointer', fontSize:'14px', fontWeight:'600',
                        backgroundColor: plan.featured ? BLUE       : 'transparent',
                        color:           plan.featured ? 'white'    : BLUE,
                        border:          plan.featured ? 'none'     : `1px solid ${BLUE}`,
                      }}>
                        {organization?.plan === plan.key ? 'Current Plan' : 'Upgrade to '+plan.name}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
